import React, { useState } from 'react';
import { X, RefreshCcw, Loader2 } from 'lucide-react';

export interface RenormalizeParams {
    batchSize: number;
    dryRun: boolean;
    forceReprocess: boolean;
    resetQboData: boolean;
    onlyChecks: boolean;
    excludeChecks: boolean;
    onlyNeedsReview: boolean;
}

interface RenormalizeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (params: RenormalizeParams) => void;
    isPending: boolean;
    businessName?: string;
}

const RenormalizeModal: React.FC<RenormalizeModalProps> = ({ isOpen, onClose, onConfirm, isPending, businessName }) => {
    const [params, setParams] = useState<RenormalizeParams>({
        batchSize: 100,
        dryRun: false,
        forceReprocess: true,
        resetQboData: true,
        onlyChecks: false,
        excludeChecks: false,
        onlyNeedsReview: false,
    });

    if (!isOpen) return null;

    const handleToggle = (key: keyof RenormalizeParams) => {
        setParams(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleBatchSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseInt(e.target.value);
        if (!isNaN(val)) {
            setParams(prev => ({ ...prev, batchSize: Math.max(10, Math.min(500, val)) }));
        }
    };

    return (
        <div className="fixed inset-0 z-[70] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} aria-hidden="true"></div>
                {/* This element is to trick the browser into centering the modal contents. */}
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
                    <div className="absolute top-0 right-0 pt-4 pr-4">
                        <button
                            type="button"
                            className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                            onClick={onClose}
                        >
                            <span className="sr-only">Close</span>
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="sm:flex sm:items-start w-full">
                        <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                Renormalize Vendors {businessName ? `for ${businessName}` : 'for All Businesses'}
                            </h3>

                            <div className="mt-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Batch Size (10-500)</label>
                                    <input
                                        type="number"
                                        min="10"
                                        max="500"
                                        value={params.batchSize}
                                        onChange={handleBatchSizeChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Number of records per Kafka batch</p>
                                </div>

                                <div className="space-y-3 pt-2">
                                    {[
                                        { key: 'dryRun', label: 'Dry Run', description: 'Preview without sending to Kafka' },
                                        { key: 'forceReprocess', label: 'Force Reprocess', description: 'Delete low-confidence aliases and reset transactions' },
                                        { key: 'resetQboData', label: 'Reset QBO Data', description: 'Clear QBO mapping data to force re-matching' },
                                        { key: 'onlyChecks', label: 'Only Checks', description: 'Process only check transactions' },
                                        { key: 'excludeChecks', label: 'Exclude Checks', description: 'Skip check payee resolution after renormalization' },
                                        { key: 'onlyNeedsReview', label: 'Only Needs Review', description: 'Process only unresolved transactions' },
                                    ].map((item) => (
                                        <div key={item.key} className="relative flex items-start">
                                            <div className="flex items-center h-5">
                                                <input
                                                    id={item.key}
                                                    name={item.key}
                                                    type="checkbox"
                                                    checked={params[item.key as keyof RenormalizeParams] as boolean}
                                                    onChange={() => handleToggle(item.key as keyof RenormalizeParams)}
                                                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer"
                                                />
                                            </div>
                                            <div className="ml-3 text-sm">
                                                <label htmlFor={item.key} className="font-medium text-gray-700 cursor-pointer">
                                                    {item.label}
                                                </label>
                                                <p className="text-gray-500 text-xs">{item.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-8">
                                <button
                                    onClick={() => onConfirm(params)}
                                    disabled={isPending}
                                    className="w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                >
                                    {isPending ? (
                                        <>
                                            <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                            Triggering...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCcw className="-ml-1 mr-2 h-4 w-4" />
                                            Trigger Renormalization
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RenormalizeModal;
